// Cinematic hero card for Anant CityPro Connect — sits at the TOP of Home + Dashboard
import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ChevronRight, MapPin } from 'lucide-react';

const SERVICES = [
  { icon: '🚕', label: 'Taxi' }, { icon: '🛺', label: 'Auto' }, { icon: '🏍️', label: 'Bike' },
  { icon: '🧺', label: 'Laundry' }, { icon: '🏥', label: 'Health' }, { icon: '🛍️', label: 'Retail' },
  { icon: '🌾', label: 'Mill' }, { icon: '✂️', label: 'Tailor' }, { icon: '🔧', label: 'Repair' },
  { icon: '🛒', label: 'Grocery' }, { icon: '🥛', label: 'Dairy' }, { icon: '💧', label: 'Water' },
  { icon: '💇', label: 'Salon' }, { icon: '📚', label: 'Tuition' }, { icon: '💸', label: 'Cash' },
];

// Unsplash direct URL: colourful Indian street market at dusk with lights
const HERO_IMG = 'https://images.unsplash.com/photo-1524492412937-b28074a5d7da?w=1400&q=70&auto=format&fit=crop';

const CityProHeroWidget = () => (
  <section className="px-4 pt-4" data-testid="citypro-hero-widget">
    <div className="max-w-7xl mx-auto">
      <Link to="/citypro" className="block group">
        <div className="relative overflow-hidden rounded-3xl border-2 border-emerald-400/60 shadow-2xl shadow-emerald-500/20 hover:shadow-emerald-500/50 hover:scale-[1.005] transition-all min-h-[280px] sm:min-h-[340px]">
          {/* BG image */}
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_IMG})` }} />
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-950/95 via-teal-950/85 to-cyan-950/95" />
          <div className="absolute -top-16 -right-16 w-96 h-96 rounded-full bg-emerald-500/25 blur-3xl animate-pulse" />
          <div className="absolute -bottom-20 -left-20 w-96 h-96 rounded-full bg-cyan-500/25 blur-3xl" />

          {/* Content */}
          <div className="relative p-5 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
            <div className="flex-shrink-0 w-20 h-20 sm:w-28 sm:h-28 rounded-3xl bg-gradient-to-br from-emerald-400 via-teal-400 to-cyan-500 border-4 border-white/40 shadow-2xl shadow-emerald-500/60 flex items-center justify-center text-5xl sm:text-7xl group-hover:rotate-6 group-hover:scale-110 transition-transform">
              🏙️
            </div>
            <div className="flex-1 min-w-0 text-white">
              <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500 text-white text-[10px] sm:text-xs font-black uppercase tracking-widest mb-2 shadow-lg">
                <Sparkles className="w-3 h-3" /> NEW · Anant CityPro Connect
              </div>
              <h2 className="text-3xl sm:text-5xl font-black tracking-tight leading-none">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-emerald-300 via-teal-200 to-cyan-300">Aapka Sheher</span>
                <span className="text-white"> · </span>
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-300 via-white to-emerald-300">Aapki Seva</span>
              </h2>
              <p className="text-sm sm:text-base opacity-90 mt-2 leading-relaxed">
                Taxi · Auto · Laundry · Doctor · Grocery · Mill · Tailor · Cash Exchange — sab kuch <b className="text-emerald-300">ek app me</b>.
                Sirf <b className="text-amber-300">AT Wallet</b> se pay karo · <b className="text-cyan-300">33% CRR</b> aapke 7-level cascade me flow hoga.
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5 max-w-xl">
                {SERVICES.map((s) => (
                  <span key={s.label} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-[10px] sm:text-xs font-bold">
                    <span>{s.icon}</span> {s.label}
                  </span>
                ))}
              </div>
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <span className="text-[10px] sm:text-xs px-2 py-1 rounded-lg bg-emerald-500/25 border border-emerald-400/40 font-black text-emerald-200">✓ Offline-First PWA</span>
                <span className="text-[10px] sm:text-xs px-2 py-1 rounded-lg bg-amber-500/25 border border-amber-400/40 font-black text-amber-200">✓ Pay-After-Earn Drivers</span>
                <span className="text-[10px] sm:text-xs px-2 py-1 rounded-lg bg-cyan-500/25 border border-cyan-400/40 font-black text-cyan-200"><MapPin className="w-3 h-3 inline" /> Hyper-Local</span>
              </div>
            </div>
            <div className="hidden sm:flex flex-col items-end gap-2 shrink-0">
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest opacity-70 font-black">Launch Offer</div>
                <div className="text-2xl font-black text-emerald-300">FREE Booking</div>
                <div className="text-[10px] opacity-70">First 100 users · Cash Center 3% only</div>
              </div>
              <div className="inline-flex items-center gap-1 px-4 py-2.5 rounded-2xl bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 text-white font-black text-sm shadow-xl shadow-emerald-500/50 group-hover:translate-x-1 transition">
                Explore Now <ChevronRight className="w-4 h-4" />
              </div>
            </div>
          </div>
          {/* Mobile CTA */}
          <div className="sm:hidden relative px-5 pb-5">
            <div className="w-full text-center px-4 py-3 rounded-2xl bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 text-white font-black shadow-xl shadow-emerald-500/50">
              🚀 Explore CityPro Connect →
            </div>
          </div>
        </div>
      </Link>
    </div>
  </section>
);

export default CityProHeroWidget;
